/**
 * Quick view panel that shows on hover over company in sidebar
 * Displays company summary, KB status, and recent updates
 */

import { Link } from 'react-router-dom';
import { CompanyListItem, Materiality } from '../../types';

interface CompanyUpdateInfo {
  id: string;
  unread_count: number;
  highest_materiality: Materiality | null;
  latest_event: {
    title: string;
    event_type: string;
    published_at: string;
  } | null;
}

interface CompanyQuickViewProps {
  company: CompanyListItem;
  updateInfo: CompanyUpdateInfo | null;
  onClose: () => void;
}

const companyTypeLabels: Record<string, string> = {
  public: 'Public',
  pe_backed: 'PE-Backed',
  private: 'Private',
  subsidiary: 'Subsidiary',
};

const getMaterialityColor = (materiality: Materiality | null): string => {
  switch (materiality) {
    case 'critical':
      return 'text-red-600';
    case 'high':
      return 'text-red-500';
    case 'medium':
      return 'text-amber-500';
    default:
      return 'text-gray-500';
  }
};

const getMaterialityIcon = (materiality: Materiality | null): string => {
  switch (materiality) {
    case 'critical':
    case 'high':
      return '!';
    case 'medium':
      return '•';
    default:
      return '○';
  }
};

export function CompanyQuickView({ company, updateInfo, onClose }: CompanyQuickViewProps) {
  const kbPercent = company.kb_completeness ? Math.round(company.kb_completeness * 100) : 0;

  return (
    <div
      className="absolute left-full top-0 ml-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
      onMouseEnter={() => {}} // Keep open when hovering the panel
      onMouseLeave={onClose}
    >
      {/* Header */}
      <div className="p-3 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-medium text-gray-900">{company.name}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              {company.ticker && (
                <span className="text-xs text-gray-500">{company.ticker}</span>
              )}
              <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                {companyTypeLabels[company.company_type] || company.company_type}
              </span>
            </div>
          </div>
          <Link
            to={`/companies/${company.id}`}
            className="text-gray-400 hover:text-primary-600"
            title="View details"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </Link>
        </div>
      </div>

      {/* KB Status */}
      <div className="px-3 py-2 border-b border-gray-100">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500">Knowledge Base</span>
          <span className="text-xs font-medium text-gray-700">{kbPercent}%</span>
        </div>
        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              company.kb_is_building
                ? 'bg-amber-400 animate-pulse'
                : kbPercent >= 70
                ? 'bg-green-500'
                : kbPercent >= 40
                ? 'bg-amber-500'
                : 'bg-red-500'
            }`}
            style={{ width: `${kbPercent}%` }}
          />
        </div>
        {company.kb_is_building && (
          <p className="text-xs text-amber-600 mt-1">Building...</p>
        )}
      </div>

      {/* Recent Updates */}
      {updateInfo && updateInfo.unread_count > 0 && (
        <div className="px-3 py-2 border-b border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">Recent Updates</span>
            <span className={`text-xs font-medium ${getMaterialityColor(updateInfo.highest_materiality)}`}>
              {updateInfo.unread_count} new
            </span>
          </div>
          {updateInfo.latest_event && (
            <div className="bg-gray-50 rounded p-2">
              <div className="flex items-start gap-1.5">
                <span className={`text-sm ${getMaterialityColor(updateInfo.highest_materiality)}`}>
                  {getMaterialityIcon(updateInfo.highest_materiality)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-700 line-clamp-2">
                    {updateInfo.latest_event.title}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatRelativeTime(updateInfo.latest_event.published_at)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="p-3 flex gap-2">
        <Link
          to={`/companies/${company.id}`}
          className="flex-1 text-center px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 transition-colors"
        >
          View Details
        </Link>
        <Link
          to={`/companies/${company.id}?tab=reports`}
          className="flex-1 text-center px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded hover:bg-primary-100 transition-colors"
        >
          Reports
        </Link>
      </div>
    </div>
  );
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'Just now';
  if (diffHours === 1) return '1 hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

export default CompanyQuickView;
